import type { ReactNode } from 'react';
import { X } from 'lucide-react';

interface AttachedCardProps {
  /** Fully-styled title node — e.g. a truncating <Link>. */
  title: ReactNode;
  description?: string;
  badge?: ReactNode;
  onRemove: () => void;
  removeLabel?: string;
  /** Extra controls rendered below the title (e.g. the KB write toggle). */
  children?: ReactNode;
}

/**
 * Compact bordered card representing one attached resource (skill, tool,
 * sub-agent, KB) in the agent editor. Mirrors the read-only look in
 * AgentDetail but is bordered and removable.
 */
export function AttachedCard({
  title,
  description,
  badge,
  onRemove,
  removeLabel = 'Remove',
  children,
}: AttachedCardProps) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            {title}
            {badge}
          </div>
          {description && (
            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel}
          className="-mr-1 -mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {children}
    </div>
  );
}
